import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
    compName_character,
    livePortsNumber,
} from '../logic/panels/helpers/LiveButton'

/**
 * LiveButtonControl – Toggle button for PlayScreen with live hardware output.
 *
 * Click to toggle ON (sends savedBytes from admin) / OFF (sends 0).
 * Uses the same Live protocol (LS / LW) as MotorSlider.
 *
 * Props:
 *   comp           – full component object from play data
 *   outputStates   – current output state map
 *   onChange        – (mapping, value) callback
 *   resolveImage   – image resolver function
 *   highlightClass – CSS class string for step highlighting
 *   style          – positioning style object
 */

const DUAL_PORT_MOTORS = [
    'mini_geared_motor',
    'geared_motor',
    'dc_motor',
    'dynamex_motor',
]

function LiveButtonControl({
    comp,
    outputStates,
    onChange,
    resolveImage,
    highlightClass,
    style,
    isLiveStep,
    onRequestActivate,
}) {
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)

    const componentType = (comp.name || '').toLowerCase()
    const isDualPort = DUAL_PORT_MOTORS.includes(componentType)
    const port = comp.port

    // RGB mode: comp.name === 'RGB' → uses dedicated Toki LW protocol [76,87,76,75,0,R,G,B]
    const isRGBMode = componentType === 'rgb'
    // S1/S2 are display labels; servo_motor1/servo_motor2 are the keys in livePortsNumber.Toki
    const resolvedPort = isRGBMode
        ? port === 'S1'
            ? 'servo_motor1'
            : port === 'S2'
              ? 'servo_motor2'
              : port
        : port

    // free=true → use fixedBytes (recorded); free=false → use direct (raw paste)
    const free = comp.properties.free ?? true
    const fixedBytes = comp.properties.fixedBytes || null
    const directBytes =
        comp.properties.direct && comp.properties.direct.length > 0
            ? comp.properties.direct
            : null
    const rawBytes = free ? null : directBytes
    const savedBytes = free ? fixedBytes : null

    const { port1, port2 } = useMemo(() => {
        if (!isDualPort || !port) return { port1: port, port2: null }
        const match = port.match(/^([A-Za-z]+)(\d+)$/)
        if (match) {
            const letter = match[1]
            const num = parseInt(match[2])
            return { port1: port, port2: `${letter}${num + 1}` }
        }
        return { port1: port + '1', port2: port + '2' }
    }, [port, isDualPort])

    const liveChange = useCallback(
        (targetPort, value) => {
            if (!worker || !webSerial?.isConnected) return
            const connectedDevice = sessionStorage.getItem('connectedDevice')
            if (!connectedDevice) return

            let adjustedValue = value
            if (componentType === 'servo_motor_270') {
                adjustedValue = Math.round(0.6667 * value)
            } else if (componentType === 'servo_motor_360') {
                adjustedValue = 100 + value
            }

            const charCode =
                compName_character[componentType] || 'P'.charCodeAt(0)
            const portNum =
                livePortsNumber?.[connectedDevice]?.[targetPort] ||
                livePortsNumber?.Ace?.[targetPort]
            if (portNum === undefined) return

            const data = [
                'L'.charCodeAt(0),
                'W'.charCodeAt(0),
                charCode,
                portNum,
                adjustedValue,
            ]
            worker.postMessage({ type: 'writeArray', value: data })
        },
        [componentType, worker, webSerial]
    )

    const sendLiveValue = useCallback(
        async (value) => {
            if (!port) return

            if (isDualPort) {
                if (value < 0) {
                    const absVal =
                        componentType === 'dynamex_motor'
                            ? Math.round(Math.abs(value) * 0.8)
                            : Math.abs(value)
                    liveChange(port1, absVal)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port2, 0)
                } else if (value > 0) {
                    const v =
                        componentType === 'dynamex_motor'
                            ? Math.round(value * 0.8)
                            : value
                    liveChange(port2, v)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port1, 0)
                } else {
                    liveChange(port2, 0)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port1, 0)
                }
            } else {
                liveChange(port, value)
            }
        },
        [port, isDualPort, port1, port2, liveChange, componentType]
    )

    const sendRGBLiveBytes = useCallback(
        (rgb) => {
            if (!worker || !webSerial?.isConnected) return
            const r = Math.min(100, Math.max(0, Number(rgb.r) || 0))
            const g = Math.min(100, Math.max(0, Number(rgb.g) || 0))
            const b = Math.min(100, Math.max(0, Number(rgb.b) || 0))
            // Toki RGB live write: [L, W, L, K, 0, R, G, B]
            worker.postMessage({
                type: 'writeArray',
                value: [76, 87, 76, 75, 0, r, g, b],
            })
        },
        [worker, webSerial]
    )

    const sendRawBytes = useCallback(async () => {
        if (!rawBytes || !worker || !webSerial?.isConnected) return
        worker.postMessage({ type: 'writeArray', value: rawBytes })
    }, [rawBytes, worker, webSerial])

    const sendSavedBytes = useCallback(async () => {
        if (!savedBytes || !worker || !webSerial?.isConnected) return
        for (const bytes of savedBytes) {
            worker.postMessage({ type: 'writeArray', value: bytes })
            await new Promise((r) => setTimeout(r, 100))
        }
    }, [savedBytes, worker, webSerial])

    const sendOffBytes = useCallback(async () => {
        if (!port || !worker || !webSerial?.isConnected) return
        if (isDualPort) {
            await sendLiveValue(0)
        } else {
            await sendLiveValue(0)
        }
    }, [port, worker, webSerial, isDualPort, sendLiveValue])

    const handleClick = useCallback(async () => {
        if (onRequestActivate && !onRequestActivate()) return
        const currentValue = outputStates[comp.mapping] || 0
        const turningOn = currentValue === 0

        // Always update the visual state
        onChange(comp.mapping, turningOn ? 1 : 0)

        // Prevent component from sending raw bytes directly if the PlayScreen global step handles it!
        if (isLiveStep) return

        // RGB mode: send the Toki 8-byte LW RGB packet directly
        if (isRGBMode) {
            if (turningOn) {
                if (savedBytes) {
                    // Use the admin-recorded fixedBytes — these already encode the exact colour.
                    await sendSavedBytes()
                } else {
                    // No fixedBytes: construct a live RGB packet from onValue.
                    // Never default to white — use black (off) if onValue not explicitly set.
                    const onValue = comp.properties.onValue || {
                        r: 0,
                        g: 0,
                        b: 0,
                    }
                    sendRGBLiveBytes(onValue)
                }
            } else {
                // Turn off: send the 8-byte all-zero RGB packet.
                sendRGBLiveBytes({ r: 0, g: 0, b: 0 })
            }
            return
        }

        if (turningOn) {
            onChange(comp.mapping, 1)
            if (rawBytes) {
                await sendRawBytes()
            } else {
                await sendSavedBytes()
            }
        } else {
            onChange(comp.mapping, 0)
            await sendOffBytes()
        }
    }, [
        outputStates,
        comp.mapping,
        comp.properties.onValue,
        comp.properties.offValue,
        onChange,
        rawBytes,
        isRGBMode,
        sendRGBLiveBytes,
        sendRawBytes,
        sendSavedBytes,
        sendOffBytes,
        onRequestActivate,
    ])

    // The button is "ON" visually if its value is non-zero
    const isBtnOn = (outputStates[comp.mapping] || 0) !== 0
    const btnImg = isBtnOn
        ? comp.properties.onImage || comp.properties.imageUrl
        : comp.properties.offImage || comp.properties.imageUrl

    return (
        <div
            key={comp.id}
            id={comp.id}
            className={`absolute-component playscreen-action-button${highlightClass}`}
            style={{ ...style, zIndex: style?.zIndex ?? 20 }}
        >
            <img
                className={comp.properties.className || ''}
                src={resolveImage(btnImg)}
                alt={comp.name}
                onPointerDown={handleClick} // Swapped from onClick
                onDragStart={(e) => e.preventDefault()} // Add this to prevent accidental dragging
                style={{ cursor: 'pointer', touchAction: 'none' }}
            />
            <span className="control-label">
                {comp.properties.label || comp.name}
            </span>
        </div>
    )
}

export default LiveButtonControl
